'use client'

import { useEffect, useState } from 'react'

import useAccountId from '@/buyer-portal/helpers/useAccountId'

const STORAGE_KEY_PREFIX = 'bulkBidsUploadUsed:'

const getStorageKey = (accountId: string | null) =>
  `${STORAGE_KEY_PREFIX}${accountId ?? 'anonymous'}`

/**
 * Whether to show the "New" badge on the Upload Bulk Bids entry point: it
 * hides once this buyer has completed the upload flow (placed bids) once,
 * which the generic FEATURE_BADGES registry can't express — that registry
 * only gates on a fixed launch-date window, not "has used it" — so this is a
 * bespoke, localStorage-backed flag instead of a registry entry. The caller
 * is responsible for calling `markUsed` only once the flow has actually
 * completed, not merely on opening it. Defaults to hidden until the stored
 * value is read on mount, matching the registry's own hydration-safe
 * default, so server and first client render always agree and the badge
 * never flashes in before disappearing.
 */
export const useBulkBidsDiscoveryBadge = () => {
  const accountId = useAccountId()
  const [showNewBadge, setShowNewBadge] = useState(false)

  useEffect(() => {
    try {
      setShowNewBadge(localStorage.getItem(getStorageKey(accountId)) !== 'true') // oxlint-disable-line react/set-state-in-effect
    } catch {
      // Storage unavailable (privacy mode, disabled storage, etc.) — default
      // to not showing the badge rather than risk it getting stuck forever.
      setShowNewBadge(false)
    }
  }, [accountId])

  const markUsed = () => {
    setShowNewBadge(false)
    try {
      localStorage.setItem(getStorageKey(accountId), 'true')
    } catch {
      // Best-effort — if storage is unavailable, the badge just won't
      // remember the dismissal across reloads, which is harmless here.
    }
  }

  return { showNewBadge, markUsed }
}
